import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ReactComponent as Logo } from '../icons/logo_letter.svg';
import { entityService } from '../services/entityService.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { analyticsEvents } from '../config/analyticsConfig.js';
import './CreateEntity.css';

function CreateEntity() {
  usePageTitle('Criar Entidade');
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = tipo, 2 = dados básicos, 3 = endereço
  const [entityType, setEntityType] = useState(''); // company, personal, others
  const [loading, setLoading] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  
  // Dados do formulário
  const [formData, setFormData] = useState({
    name: '',
    documentType: 'CNPJ', // CNPJ, CPF, CRMV
    document: '',
    description: '',
    businessHours: '',
    phone: '',
  });
  
  // Dados de endereço
  const [addressData, setAddressData] = useState({
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    showExactLocation: true,
    coverageArea: 5, // em km, 0 = sem limite
  });

  const handleTypeSelect = (type) => {
    setEntityType(type);
    setStep(2);
    
    // Ajustar tipo de documento padrão baseado no tipo de entidade
    if (type === 'personal') {
      setFormData(prev => ({ ...prev, documentType: 'CRMV' }));
    } else if (type === 'company') {
      setFormData(prev => ({ ...prev, documentType: 'CNPJ' }));
    } else {
      setFormData(prev => ({ ...prev, documentType: 'CNPJ' }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Aplicar máscaras
    let maskedValue = value;
    
    if (name === 'document') {
      if (formData.documentType === 'CNPJ') {
        maskedValue = maskCNPJ(value);
      } else if (formData.documentType === 'CPF') {
        maskedValue = maskCPF(value);
      } else if (formData.documentType === 'CRMV') {
        maskedValue = maskCRMV(value);
      }
    } else if (name === 'phone') {
      maskedValue = maskPhone(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: maskedValue
    }));
  };
  
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    
    let maskedValue = value;
    
    if (name === 'cep') {
      maskedValue = maskCEP(value);
      
      // Se CEP completo, buscar endereço
      const cepNumbers = value.replace(/\D/g, '');
      if (cepNumbers.length === 8) {
        fetchAddressByCep(cepNumbers);
      }
    }
    
    setAddressData(prev => ({
      ...prev,
      [name]: maskedValue
    }));
  };
  
  const fetchAddressByCep = async (cep) => {
    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      
      if (!data.erro) {
        setAddressData(prev => ({
          ...prev,
          street: data.logradouro || '',
          neighborhood: data.bairro || '',
          city: data.localidade || '',
          state: data.uf || '',
        }));
      } else {
        toast.error('CEP não encontrado');
      }
    } catch (error) {
      toast.error('Erro ao buscar CEP');
    } finally {
      setLoadingCep(false);
    }
  };

  // Função para geocodificar endereço e obter lat/lng
  const geocodeAddress = async (address) => {
    try {
      const fullAddress = `${address.street}, ${address.number}, ${address.city}, ${address.state}, Brasil`;
      const apiKey = 'AIzaSyA3eQOASrGy5ysiPCB8rP3HZhQbRGv9Omw';
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(fullAddress)}&key=${apiKey}`
      );
      const data = await response.json();
      
      if (data.status === 'OK' && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        return {
          lat: location.lat,
          lng: location.lng
        };
      }
      
      return null;
    } catch (error) {
      console.error('Erro ao geocodificar:', error);
      return null;
    }
  };

  const handleDocumentTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      documentType: type,
      document: '' // Limpar documento ao trocar tipo
    }));
  };

  // Máscaras
  const maskCEP = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{5})(\d)/, '$1-$2')
      .substr(0, 9);
  };
  
  const maskCNPJ = (value) => {
    // Remove apenas formatação, preserva alfanuméricos (formato: AA.AAA.AAA/AAAA-##)
    let v = value.replace(/[.\-/]/g, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (v.length > 14) v = v.slice(0, 14);
    // Os 2 últimos caracteres só podem ser dígitos
    if (v.length > 12) {
      const prefix = v.slice(0, 12);
      const suffix = v.slice(12).replace(/\D/g, '');
      v = prefix + suffix;
    }

    if (v.length > 12) {
      return v.replace(/^(.{2})(.{3})(.{3})(.{4})(.{0,2})/, '$1.$2.$3/$4-$5');
    } else if (v.length > 8) {
      return v.replace(/^(.{2})(.{3})(.{3})(.{0,4})/, '$1.$2.$3/$4');
    } else if (v.length > 5) {
      return v.replace(/^(.{2})(.{3})(.{0,3})/, '$1.$2.$3');
    } else if (v.length > 2) {
      return v.replace(/^(.{2})(.{0,3})/, '$1.$2');
    }
    return v;
  };

  const maskCPF = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2')
      .substr(0, 14);
  };

  const maskCRMV = (value) => {
    return value
      .toUpperCase()
      .replace(/[^0-9A-Z/]/g, '')
      .replace(/^(\d{7})(\/)/, '$1$2')
      .substr(0, 10); // 0000000/UF
  };

  const maskPhone = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .substr(0, 15);
  };

  const handleContinueToAddress = (e) => {
    e.preventDefault();
    
    // Validações
    if (!formData.name.trim()) {
      toast.error('Por favor, preencha o nome');
      return;
    }
    
    if (!formData.document.trim()) {
      toast.error(`Por favor, preencha o ${formData.documentType}`);
      return;
    }
    
    if (!formData.phone.trim()) {
      toast.error('Por favor, preencha o telefone');
      return;
    }
    
    setStep(3);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações de endereço
    if (!addressData.cep.trim()) {
      toast.error('Por favor, preencha o CEP');
      return;
    }
    
    if (!addressData.street.trim()) {
      toast.error('Por favor, preencha o endereço');
      return;
    }
    
    if (!addressData.number.trim()) {
      toast.error('Por favor, preencha o número');
      return;
    }
    
    setLoading(true);
    
    try {
      // Geocodificar endereço para obter lat/lng
      const coordinates = await geocodeAddress(addressData);
      
      if (!coordinates) {
        toast.error('Não foi possível obter as coordenadas do endereço');
        setLoading(false);
        return;
      }
      
      // Mapear tipo de entidade para formato da API
      let apiType = 'company';
      if (entityType === 'personal') {
        apiType = 'personal';
      } else if (entityType === 'others') {
        apiType = 'others';
      }
      
      // Preparar dados no formato da API
      const entityData = {
        name: formData.name,
        description: formData.description || '',
        documentType: formData.documentType,
        documentNumber: formData.document,
        phone: `+55${formData.phone.replace(/\D/g, '')}`,
        address: addressData.street,
        addressNumber: addressData.number,
        addressComplement: addressData.complement || null,
        addressCity: addressData.city,
        addressUF: addressData.state,
        addressNeighborhood: addressData.neighborhood,
        addressZIP: addressData.cep.replace(/\D/g, ''),
        showAddress: addressData.showExactLocation,
        lat: coordinates?.lat ? Number(coordinates.lat).toFixed(5) : null,
        long: coordinates?.lng ? Number(coordinates.lng).toFixed(5) : null,
        serviceRange: addressData.coverageArea,
        serviceRangeActive: !addressData.showExactLocation,
        type: apiType,
        openingHours: formData.businessHours || '',
        images: [],
        active: false,
      };
      
      await entityService.createEntity(entityData);
      
      analyticsEvents.createEntity();
      toast.success('Negócio criado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Erro ao criar negócio');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(2);
    } else if (step === 2) {
      setStep(1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleClose = () => {
    navigate('/dashboard');
  };

  const getDocumentPlaceholder = () => {
    if (formData.documentType === 'CNPJ') return '00.000.000/0001-00';
    if (formData.documentType === 'CPF') return '000.000.000-00';
    if (formData.documentType === 'CRMV') return '0000000/UF';
    return '';
  };

  const getDocumentLabel = () => {
    if (entityType === 'personal') {
      return formData.documentType === 'CRMV' ? 'Currículo' : 'Descrição';
    }
    return 'Descrição';
  };

  return (
    <div className="create-entity-container">
      <div className="create-entity-background" />
      
      <div className="create-entity-card">
        {/* Step 1: Escolher tipo */}
        {step === 1 && (
          <>
            <button onClick={handleClose} className="close-button" aria-label="Fechar">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6L18 18" />
              </svg>
            </button>
            <div className="entity-type-selection">
              <h1 className="create-entity-title">Qual o tipo do seu negócio?</h1>
            
            <div className="entity-types">
              <button
                className="entity-type-card"
                onClick={() => handleTypeSelect('company')}
              >
                <div className="entity-type-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <span className="entity-type-label">Clínica ou Hospital Veterinário</span>
              </button>

              <button
                className="entity-type-card"
                onClick={() => handleTypeSelect('personal')}
              >
                <div className="entity-type-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <span className="entity-type-label">Profissional Autônomo</span>
              </button>

              <button
                className="entity-type-card"
                onClick={() => handleTypeSelect('others')}
              >
                <div className="entity-type-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                  </svg>
                </div>
                <span className="entity-type-label">Outros</span>
              </button>
            </div>
            </div>
          </>
        )}

        {/* Step 2: Formulário */}
        {step === 2 && (
          <>
            <button onClick={handleClose} className="close-button" aria-label="Fechar">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6L18 18" />
              </svg>
            </button>
            <div className="entity-form-section">
              <h1 className="create-entity-title">
              {entityType === 'company' && 'Clínica ou Hospital Veterinário'}
              {entityType === 'personal' && 'Profissional Autônomo'}
              {entityType === 'others' && 'Outros'}
            </h1>

            <form onSubmit={handleContinueToAddress} className="entity-form">
              {/* Nome */}
              <div className="form-group">
                <label className="form-label">Nome<span className="required">*</span></label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Ex: Clínica Veterinária São Francisco"
                  className="form-input"
                  disabled={loading}
                />
              </div>

              {/* Documento (CNPJ/CPF/CRMV) */}
              <div className="form-group">
                <label className="form-label">
                  {entityType === 'personal' ? 'CRMV' : formData.documentType}<span className="required">*</span>
                </label>
                
                {entityType === 'others' && (
                  <div className="document-type-selector">
                    <button
                      type="button"
                      className={`doc-type-option ${formData.documentType === 'CNPJ' ? 'active' : ''}`}
                      onClick={() => handleDocumentTypeChange('CNPJ')}
                      disabled={loading}
                    >
                      CNPJ
                    </button>
                    <button
                      type="button"
                      className={`doc-type-option ${formData.documentType === 'CPF' ? 'active' : ''}`}
                      onClick={() => handleDocumentTypeChange('CPF')}
                      disabled={loading}
                    >
                      CPF
                    </button>
                  </div>
                )}
                
                <input
                  type="text"
                  name="document"
                  value={formData.document}
                  onChange={handleInputChange}
                  placeholder={getDocumentPlaceholder()}
                  className="form-input"
                  disabled={loading}
                />
              </div>

              {/* Descrição/Currículo */}
              <div className="form-group">
                <label className="form-label">{getDocumentLabel()}</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder={`Ex: ${entityType === 'personal' ? 'Médico veterinário formado pela USP...' : 'Atendimento 24 horas, emergências...'}`}
                  className="form-textarea"
                  rows="4"
                  disabled={loading}
                />
              </div>

              {/* Horário de Funcionamento */}
              <div className="form-group">
                <label className="form-label">Horário de Funcionamento</label>
                <input
                  type="text"
                  name="businessHours"
                  value={formData.businessHours}
                  onChange={handleInputChange}
                  placeholder="Ex: Seg-Sex: 8h-18h, Sáb: 9h-13h"
                  className="form-input"
                  disabled={loading}
                />
              </div>

              {/* Telefone */}
              <div className="form-group">
                <label className="form-label">Telefone<span className="required">*</span></label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Ex: (11) 98765-4321"
                  className="form-input"
                  disabled={loading}
                />
              </div>

              {/* Botões */}
              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleBack}
                  className="btn-secondary"
                  disabled={loading}
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  Continuar
                </button>
              </div>
            </form>
            </div>
          </>
        )}

        {/* Step 3: Endereço */}
        {step === 3 && (
          <>
            <button onClick={handleClose} className="close-button" aria-label="Fechar">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6L18 18" />
              </svg>
            </button>
            <div className="entity-form-section">
              <h1 className="create-entity-title">Endereço</h1>

            <form onSubmit={handleSubmit} className="entity-form">
              {/* CEP */}
              <div className="form-group">
                <label className="form-label">CEP<span className="required">*</span></label>
                <input
                  type="text"
                  name="cep"
                  value={addressData.cep}
                  onChange={handleAddressChange}
                  placeholder="00000-000"
                  className="form-input"
                  disabled={loading || loadingCep}
                />
              </div>

              {/* Endereço */}
              <div className="form-group">
                <label className="form-label">Endereço<span className="required">*</span></label>
                <input
                  type="text"
                  name="street"
                  value={addressData.street}
                  onChange={handleAddressChange}
                  placeholder="Rua, Avenida, etc."
                  className="form-input"
                  disabled={loading || loadingCep}
                />
              </div>

              {/* Número */}
              <div className="form-group">
                <label className="form-label">Número<span className="required">*</span></label>
                <input
                  type="text"
                  name="number"
                  value={addressData.number}
                  onChange={handleAddressChange}
                  placeholder="123"
                  className="form-input"
                  disabled={loading}
                />
              </div>

              {/* Complemento */}
              <div className="form-group">
                <label className="form-label">Complemento</label>
                <input
                  type="text"
                  name="complement"
                  value={addressData.complement}
                  onChange={handleAddressChange}
                  placeholder="Apartamento, sala, etc. (opcional)"
                  className="form-input"
                  disabled={loading}
                />
              </div>

              {/* Mostrar localização exata - apenas para profissional */}
              {entityType === 'personal' && (
                <>
                  <div className="form-group-toggle">
                    <div className="toggle-content">
                      <div className="toggle-text">
                        <label className="form-label">Mostrar localização exata</label>
                        <p className="toggle-description">
                          Deseja compartilhar sua localização exata com os clientes?
                        </p>
                      </div>
                      <div className="switch-container">
                        <input
                          type="checkbox"
                          id="showExactLocation"
                          className="switch-checkbox"
                          checked={addressData.showExactLocation}
                          onChange={(e) => setAddressData(prev => ({ ...prev, showExactLocation: e.target.checked }))}
                          disabled={loading}
                        />
                        <label htmlFor="showExactLocation" className="switch-label">
                          <span className="switch-button"></span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Área de cobertura - só exibe se localização exata estiver desativada */}
                  {!addressData.showExactLocation && (
                    <div className="form-group">
                      <label className="form-label">Área de cobertura</label>
                      <p className="form-helper-text">
                        Esta informação é disponibilizada aos clientes para que possam verificar se estão dentro de sua área de cobertura
                      </p>
                      <div className="coverage-slider">
                        <input
                          type="range"
                          min="1"
                          max="50"
                          value={addressData.coverageArea}
                          onChange={(e) => setAddressData(prev => ({ ...prev, coverageArea: parseInt(e.target.value) }))}
                          className="slider"
                          disabled={loading}
                        />
                        <div className="slider-labels">
                          <span>1 km</span>
                          <span className="slider-value">
                            {addressData.coverageArea === 50 ? 'Sem limite' : `${addressData.coverageArea} km`}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Botões */}
              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleBack}
                  className="btn-secondary"
                  disabled={loading}
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading || loadingCep}
                >
                  {loading ? (
                     <>
                      <svg className="spinner-edit" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" opacity="0.25"/>
                        <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                      </svg>
                      Criando...
                    </>
                  ) : (
                    'Criar Negócio'
                  )}
                </button>
              </div>
            </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default CreateEntity;
